// src/pages/Articulos/ImportadorArticulos.js
//
// Alta masiva de artículos, dividida en solapas por tipo.
//
// Los cuatro tipos comparten el mismo flujo —cargar Excel, previsualizar,
// corregir y confirmar—, así que la pantalla es una sola y lo que cambia sale
// de la definición del tipo, que viene del backend (GET /articulos/tipos). Un
// componente por solapa habría repetido cuatro veces la misma tabla.
//
// Qué configura cada tipo en SAP (almacenes, si gestiona stock, cómo determina
// las cuentas) vive en back/dominio/tiposArticulo.js: acá sólo se arma la UI.
import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import { parseExcelFile } from '../../utils/excelParser';
import { useToast } from '../../components/Toast/ToastProvider';
import './ImportadorArticulos.css';

function ImportadorArticulos() {
    const { showToast } = useToast();

    const [tipos, setTipos] = useState([]);
    const [tipoActivo, setTipoActivo] = useState('inventario');
    const [selectedFile, setSelectedFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [extractedData, setExtractedData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const tipo = tipos.find(t => t.id === tipoActivo);

    useEffect(() => {
        api.get('/articulos/tipos')
            .then(({ data }) => setTipos(data))
            .catch(err => {
                console.error('No se pudieron cargar los tipos de artículo:', err);
                showToast('No se pudieron cargar los tipos de artículo.', 'danger');
            });
        // showToast es estable; si se incluye, el efecto se repite en cada render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const limpiar = useCallback(() => {
        setExtractedData([]);
        setSelectedFile(null);
        const input = document.getElementById('file-upload');
        if (input) input.value = null;
    }, []);

    // Cambiar de solapa descarta lo cargado: las columnas y los obligatorios
    // son distintos, y arrastrar filas de otro tipo sólo puede terminar mal.
    const cambiarTipo = (id) => {
        if (id === tipoActivo) return;
        setTipoActivo(id);
        limpiar();
    };

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
        setExtractedData([]);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!selectedFile) {
            showToast('Por favor, selecciona un archivo antes de procesar.', 'warning');
            return;
        }
        setIsLoading(true);
        try {
            const json = await parseExcelFile(selectedFile);

            // Las columnas salen de la definición del tipo, la misma con la que
            // se arma la plantilla: así lo que se descarga y lo que se lee no
            // pueden desalinearse.
            const columnas = tipo?.columnas || [];

            const mappedData = json.map((row, index) => {
                const fila = { id: index + 1 };
                columnas.forEach(c => { fila[c.clave] = row[c.encabezado] ?? ''; });
                // Si el tipo tiene un grupo por defecto y el Excel no lo trae.
                if (!fila.ItmsGrpCod && tipo?.grupoPorDefecto) fila.ItmsGrpCod = tipo.grupoPorDefecto;
                return fila;
            });

            // Si no se reconoció ninguna columna, el Excel no es el que espera
            // esta solapa: conviene decirlo en vez de mostrar una tabla vacía.
            const reconocidas = columnas.filter(c => json.some(r => r[c.encabezado] !== undefined));
            if (json.length > 0 && reconocidas.length === 0) {
                showToast(
                    `El archivo no tiene ninguna de las columnas esperadas. Descargá la plantilla de "${tipo.titulo}".`,
                    'warning', 9000
                );
                return;
            }

            setExtractedData(mappedData);
        } catch (error) {
            showToast(error.message, 'danger');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClearData = () => {
        if (window.confirm('¿Eliminar datos cargados?')) limpiar();
    };

    /**
     * Arma y descarga la plantilla del tipo activo.
     *
     * Los encabezados salen de la definición del backend, la misma que usa el
     * importador para leer el archivo: si se escribieran a mano acá, alcanzaría
     * una diferencia de mayúsculas para que la columna no se encuentre y el
     * dato entre vacío sin ningún error visible.
     */
    const descargarPlantilla = async () => {
        if (!tipo) return;
        try {
            const columnas = tipo.columnas || [];
            const encabezados = columnas.map(c => c.encabezado);

            // Una fila de ejemplo, para que se vea el formato esperado de cada
            // columna. Hay que borrarla antes de subir, y la instrucción lo dice.
            const ejemplo = {};
            columnas.forEach(c => { ejemplo[c.encabezado] = c.ejemplo || ''; });

            const wb = XLSX.utils.book_new();

            const hoja = XLSX.utils.json_to_sheet([ejemplo], { header: encabezados });
            hoja['!cols'] = columnas.map(c => ({ wch: Math.max(14, c.encabezado.length + 2, String(c.ejemplo || '').length + 2) }));
            XLSX.utils.book_append_sheet(wb, hoja, 'Articulos');

            const instrucciones = [
                [`Plantilla — ${tipo.titulo}`],
                [],
                [tipo.descripcion],
                [],
                ['CÓMO USAR'],
                ['1. Borrá la fila de ejemplo de la hoja "Articulos".'],
                ['2. Completá una fila por artículo.'],
                ['3. Subí el archivo en la solapa que corresponde al tipo.'],
                ['4. Antes de crear vas a poder revisar y corregir en pantalla.'],
                [],
                ['COLUMNAS'],
                ['Columna', '¿Obligatoria?', 'Qué va'],
                ...columnas.map(c => [c.encabezado, c.obligatorio ? 'SÍ' : 'No', c.ayuda || '']),
                [],
                ['QUÉ CONFIGURA ESTE TIPO EN SAP'],
                ['Almacenes', tipo.almacenes.length ? tipo.almacenes.join(', ') : 'ninguno (no lleva stock)'],
                ...(tipo.grupoPorDefecto ? [['Familia por defecto', String(tipo.grupoPorDefecto)]] : []),
                ...(tipo.pideStockMinimo
                    ? [['Stock mínimo', 'Se carga en el almacén de consignación. El máximo queda en 0 para no topear las recepciones.']]
                    : []),
                ...(tipo.pideProveedor
                    ? [['Proveedor', 'Queda como proveedor principal del artículo. De él se compra la reposición.']]
                    : []),
                [],
                ['Los encabezados de esta plantilla son los que el importador reconoce.'],
                ['Si los cambiás, puede que no encuentre la columna.']
            ];

            const hojaInstr = XLSX.utils.aoa_to_sheet(instrucciones);
            hojaInstr['!cols'] = [{ wch: 24 }, { wch: 16 }, { wch: 70 }];
            XLSX.utils.book_append_sheet(wb, hojaInstr, 'Instrucciones');

            XLSX.writeFile(wb, `Plantilla ${tipo.titulo}.xlsx`);
            showToast('Plantilla descargada.', 'success');
        } catch (err) {
            console.error('Error generando la plantilla:', err);
            showToast('No se pudo generar la plantilla.', 'danger');
        }
    };

    const handleFieldChange = (id, fieldName, newValue) => {
        setExtractedData(d => d.map(r => r.id === id ? { ...r, [fieldName]: newValue } : r));
    };

    const handleDeleteRow = (id) => setExtractedData(d => d.filter(r => r.id !== id));

    // Lo que falta antes de poder crear. Se valida acá y también en el backend:
    // esto es comodidad, el control de verdad está del otro lado.
    const filasIncompletas = extractedData.filter(r =>
        !r.ItemCode || !r.ItemName || !r.ItmsGrpCod
        || (tipo?.pideStockMinimo && !(Number(r.StockMinimo) > 0))
        || (tipo?.pideProveedor && !String(r.Proveedor || '').trim())
    );

    const handleConfirmCreate = async () => {
        setIsLoading(true);

        try {
            const { data: result } = await api.post('/articulos/masivo', {
                articulos: extractedData,
                tipo: tipoActivo
            });

            const erroresArticulos = result.detallesError.filter(e => !e.itemCode.startsWith('IMPUESTO_') && !e.itemCode.startsWith('GLOBAL'));
            const erroresImpuestos = result.detallesError.filter(e => e.itemCode.startsWith('IMPUESTO_') || e.itemCode.startsWith('GLOBAL'));

            let msg = `¡Proceso completado!\n✅ Creados: ${result.creados}`;

            if (erroresArticulos.length > 0) {
                msg += `\n\n❌ Fallaron ${erroresArticulos.length} artículos:\n`;
                erroresArticulos.slice(0, 5).forEach(err => {
                    msg += `- ${err.itemCode}: ${err.error}\n`;
                });
            }
            if (erroresImpuestos.length > 0) {
                msg += `\n⚠️ ${erroresImpuestos.length} problema(s) con impuestos.`;
            }

            showToast(msg, erroresArticulos.length > 0 ? 'warning' : 'success', 12000);

            if (erroresArticulos.length > 0) {
                // Quedan sólo las que fallaron, para corregirlas y reintentar.
                const failedItemCodes = erroresArticulos.map(err => err.itemCode);
                setExtractedData(currentData =>
                    currentData.filter(row => failedItemCodes.includes(row.ItemCode))
                );
            } else {
                limpiar();
            }
        } catch (error) {
            showToast(`Error: ${error.response?.data?.error || error.message}`, 'danger', 10000);
        } finally {
            setIsLoading(false);
            setIsModalOpen(false);
        }
    };

    return (
        <div className="container-fluid importer-main">
            <h1 className="mt-4 mb-0">Importador Masivo de Artículos</h1>

            {/* --- Solapas por tipo --- */}
            <ul className="nav nav-tabs mt-3">
                {tipos.map(t => (
                    <li className="nav-item" key={t.id}>
                        <button
                            className={`nav-link ${tipoActivo === t.id ? 'active' : ''}`}
                            onClick={() => cambiarTipo(t.id)}
                            type="button"
                        >
                            <i className={`bi ${t.icono} me-1`}></i>
                            {t.titulo}
                            {/* La solapa se ve y se puede abrir, pero no deja
                                cargar: así se sabe que el tipo viene, sin que
                                alguien lo use creyendo que está terminado. */}
                            {t.disponible === false && (
                                <span className="badge bg-secondary-subtle text-secondary-emphasis fw-normal ms-2">
                                    Próximamente
                                </span>
                            )}
                        </button>
                    </li>
                ))}
            </ul>

            <main className="pt-3">
                {tipo && tipo.disponible === false && (
                    <section className="card-section">
                        <div className="d-flex align-items-start gap-3">
                            <i className={`bi ${tipo.icono} fs-2 text-secondary`}></i>
                            <div>
                                <h5 className="mb-1">
                                    {tipo.titulo}
                                    <span className="badge bg-secondary-subtle text-secondary-emphasis fw-normal ms-2">
                                        Próxima entrega
                                    </span>
                                </h5>
                                <p className="text-muted mb-2">{tipo.descripcion}</p>
                                <div className="alert alert-warning py-2 px-3 mb-0 small">
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    {tipo.aConfirmar}
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {tipo && tipo.disponible !== false && (
                    <section className="card-section upload-section">
                        <form onSubmit={handleSubmit}>
                            <div className="card-title"><i className="bi bi-gear-fill"></i> Paso 1: Configuración</div>

                            <div className="mb-4 p-3 bg-light rounded border">
                                <div className="fw-bold mb-1">
                                    <i className={`bi ${tipo.icono} me-2`}></i>
                                    {tipo.titulo}
                                </div>
                                <div className="text-muted small">{tipo.descripcion}</div>

                                <div className="d-flex flex-wrap gap-3 mt-2 small">
                                    <span className="text-muted">
                                        <strong>Almacenes:</strong>{' '}
                                        {tipo.almacenes.length ? tipo.almacenes.join(', ') : 'ninguno (no lleva stock)'}
                                    </span>
                                    {tipo.grupoPorDefecto && (
                                        <span className="text-muted">
                                            <strong>Familia por defecto:</strong> {tipo.grupoPorDefecto}
                                        </span>
                                    )}
                                </div>

                                {/* Columnas que espera esta solapa, a la vista: es la
                                    primera duda de quien arma el Excel. */}
                                <div className="mt-3">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                        <span className="small fw-semibold text-muted text-uppercase" style={{letterSpacing: '.03em'}}>
                                            Columnas del Excel
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={descargarPlantilla}
                                        >
                                            <i className="bi bi-download me-1"></i>
                                            Descargar plantilla
                                        </button>
                                    </div>

                                    <div className="d-flex flex-wrap gap-2">
                                        {(tipo.columnas || []).map(c => (
                                            <span
                                                key={c.clave}
                                                className={`badge fw-normal ${c.obligatorio
                                                    ? 'bg-primary-subtle text-primary-emphasis border border-primary-subtle'
                                                    : 'bg-light text-secondary border'}`}
                                                title={c.ayuda}
                                            >
                                                {c.encabezado}
                                                {c.obligatorio && <span className="ms-1">*</span>}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="text-muted mt-2" style={{fontSize: '.8rem'}}>
                                        <span className="text-primary-emphasis">*</span> obligatoria ·
                                        pasá el mouse por cada una para ver qué va
                                    </div>
                                </div>

                                {tipo.aConfirmar && (
                                    <div className="alert alert-warning py-2 px-3 mt-2 mb-0 small">
                                        <i className="bi bi-exclamation-triangle me-1"></i>
                                        {tipo.aConfirmar}
                                    </div>
                                )}
                            </div>

                            <div className="upload-controls">
                                <label htmlFor="file-upload" className="custom-file-upload">
                                    <i className="bi bi-file-earmark-excel me-2"></i> Seleccionar Excel
                                </label>
                                <input id="file-upload" type="file" accept=".xlsx, .xls, .csv" onChange={handleFileChange} />
                                <span className="file-name ms-3 fst-italic text-secondary">
                                    {selectedFile ? selectedFile.name : 'Ningún archivo seleccionado'}
                                </span>
                                <button type="submit" className="btn btn-primary ms-auto" disabled={isLoading || !selectedFile}>
                                    {isLoading ? 'Procesando...' : 'Cargar y Previsualizar'}
                                </button>
                            </div>
                        </form>
                    </section>
                )}

                {extractedData.length > 0 && tipo && (
                    <section className="card-section data-table-section">
                        <div className="card-table-header">
                            <div className="card-title">
                                <i className="bi bi-table"></i> Paso 2: Datos ({tipo.titulo})
                            </div>
                            <div className="header-actions">
                                <button
                                    className="action-btn add"
                                    onClick={() => setIsModalOpen(true)}
                                    disabled={filasIncompletas.length > 0}
                                    title={filasIncompletas.length > 0
                                        ? 'Hay filas con datos obligatorios sin completar'
                                        : undefined}
                                >
                                    <i className="bi bi-check-lg"></i> Confirmar
                                </button>
                                <button className="action-btn delete" onClick={handleClearData}>
                                    <i className="bi bi-trash"></i> Borrar
                                </button>
                            </div>
                        </div>

                        {filasIncompletas.length > 0 && (
                            <div className="alert alert-warning py-2 px-3 mb-3 small">
                                <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                <strong>{filasIncompletas.length}</strong> fila(s) con datos obligatorios
                                sin completar. Se marcan en la tabla.
                            </div>
                        )}

                        <div className="table-responsive">
                            <table className="table table-striped table-bordered table-hover align-middle" style={{fontSize: '0.9rem'}}>
                                <thead className="table-light">
                                    <tr>
                                        <th style={{width: '50px'}}>ID</th>
                                        <th style={{minWidth:'140px'}}>Código</th>
                                        <th style={{minWidth:'380px'}}>Descripción</th>
                                        <th style={{minWidth:'90px'}}>Familia</th>
                                        <th style={{minWidth:'90px'}}>Cat.</th>
                                        <th style={{minWidth:'80px'}}>UM</th>
                                        <th style={{minWidth:'90px'}}>Cant.</th>
                                        <th style={{minWidth:'80px'}}>UM Vta</th>
                                        {tipo.pideStockMinimo && <th style={{minWidth:'100px'}}>Stock mín.</th>}
                                        {tipo.pideProveedor && <th style={{minWidth:'120px'}}>Proveedor</th>}
                                        <th style={{width: '60px'}}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {extractedData.map((row) => {
                                        const incompleta = filasIncompletas.some(f => f.id === row.id);
                                        return (
                                            <tr key={row.id} className={incompleta ? 'table-warning' : undefined}>
                                                <td className="text-center fw-bold text-muted">{row.id}</td>
                                                <td><input value={row.ItemCode} onChange={e => handleFieldChange(row.id, 'ItemCode', e.target.value)} className="form-control form-control-sm" /></td>
                                                <td>
                                                    <textarea
                                                        value={row.ItemName}
                                                        onChange={e => handleFieldChange(row.id, 'ItemName', e.target.value)}
                                                        className="form-control form-control-sm"
                                                        rows="3"
                                                        style={{resize: 'vertical'}}
                                                    />
                                                </td>
                                                <td><input value={row.ItmsGrpCod} onChange={e => handleFieldChange(row.id, 'ItmsGrpCod', e.target.value)} className="form-control form-control-sm text-center" /></td>
                                                <td><input value={row.U_Categoria} onChange={e => handleFieldChange(row.id, 'U_Categoria', e.target.value)} className="form-control form-control-sm text-center" /></td>
                                                <td><input value={row.BuyUnitMsr} onChange={e => handleFieldChange(row.id, 'BuyUnitMsr', e.target.value)} className="form-control form-control-sm text-center" /></td>
                                                <td><input value={row.NumInBuy} onChange={e => handleFieldChange(row.id, 'NumInBuy', e.target.value)} className="form-control form-control-sm text-center" /></td>
                                                <td><input value={row.SalUnitMsr} onChange={e => handleFieldChange(row.id, 'SalUnitMsr', e.target.value)} className="form-control form-control-sm text-center" /></td>

                                                {tipo.pideStockMinimo && (
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={row.StockMinimo}
                                                            onChange={e => handleFieldChange(row.id, 'StockMinimo', e.target.value)}
                                                            className="form-control form-control-sm text-center"
                                                        />
                                                    </td>
                                                )}
                                                {tipo.pideProveedor && (
                                                    <td>
                                                        <input
                                                            value={row.Proveedor}
                                                            onChange={e => handleFieldChange(row.id, 'Proveedor', e.target.value)}
                                                            className="form-control form-control-sm text-center"
                                                            placeholder="P000656"
                                                        />
                                                    </td>
                                                )}

                                                <td className="text-center">
                                                    <button className="btn btn-outline-danger btn-sm border-0" onClick={() => handleDeleteRow(row.id)} title="Eliminar">
                                                        <i className="bi bi-trash-fill"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}
            </main>

            {isModalOpen && tipo && (
                <div className="modal-backdrop">
                    <div className="modal-content">
                        <h4>Confirmar Carga</h4>
                        <p>
                            Vas a crear <strong>{extractedData.length}</strong> artículo(s) de
                            tipo <strong>{tipo.titulo}</strong>.
                        </p>
                        <div className="bg-light border rounded p-2 mb-3 small">
                            <div><span className="text-muted">Almacenes:</span> {tipo.almacenes.join(', ') || 'ninguno'}</div>
                            {tipo.pideStockMinimo && (
                                <div><span className="text-muted">Con stock mínimo en:</span> CONSIG</div>
                            )}
                        </div>
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>Cancelar</button>
                            <button className="btn btn-success" onClick={handleConfirmCreate} disabled={isLoading}>
                                {isLoading ? 'Creando...' : 'Crear en SAP'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ImportadorArticulos;
